import React, { useState, useEffect } from 'react';
import { userManagementAPI } from '../api';

interface ManagedUser {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  auth_provider: string;
  profile_picture_url?: string;
  last_login_at?: string;
  created_at: string;
}

const ROLES = ['admin', 'manager', 'staff', 'viewer'];

const roleBadgeColors: Record<string, { bg: string; color: string }> = {
  admin: { bg: '#fee2e2', color: '#991b1b' },
  manager: { bg: '#fef3c7', color: '#92400e' },
  staff: { bg: '#d1fae5', color: '#065f46' },
  viewer: { bg: '#f3f4f6', color: '#374151' },
  warehouse_staff: { bg: '#d1fae5', color: '#065f46' },
};

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [sortField, setSortField] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await userManagementAPI.listUsers();
      setUsers(res.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId: number, newRole: string) => {
    try {
      setSaving(userId);
      await userManagementAPI.updateUser(userId, { role: newRole });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(null);
    }
  };

  const handleToggleActive = async (userId: number, isActive: boolean) => {
    try {
      setSaving(userId);
      await userManagementAPI.updateUser(userId, { is_active: isActive });
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: isActive } : u));
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update user status');
    } finally {
      setSaving(null);
    }
  };

  const filteredUsers = users.filter(u => {
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!u.username.toLowerCase().includes(s) && !u.email.toLowerCase().includes(s)) return false;
    }
    if (filterRole && u.role !== filterRole) return false;
    return true;
  });

  const handleSort = (field: string) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    if (!sortField) return 0;
    let aVal: any = (a as any)[sortField];
    let bVal: any = (b as any)[sortField];
    if (sortField === 'is_active') {
      aVal = aVal ? 1 : 0;
      bVal = bVal ? 1 : 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    }
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    if (typeof aVal === 'string') aVal = aVal.toLowerCase();
    if (typeof bVal === 'string') bVal = bVal.toLowerCase();
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading users...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
        <p>{error}</p>
        <button onClick={fetchUsers} style={{ marginTop: '1rem', padding: '8px 16px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ padding: '16px', borderBottom: '1px solid #e5e7eb' }}>
        <h2 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '600' }}>
          User Access Management
        </h2>
        <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#6b7280' }}>
          Manage user roles and access levels. Changes take effect on the user's next request.
        </p>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={{ flex: '1', minWidth: '200px', padding: '6px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          />
          <select
            value={filterRole}
            onChange={e => setFilterRole(e.target.value)}
            style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #d1d5db', borderRadius: '4px' }}
          >
            <option value="">All Roles</option>
            {ROLES.map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')}</option>
            ))}
          </select>
          <span style={{ padding: '6px 0', fontSize: '13px', color: '#6b7280' }}>
            {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
              <th onClick={() => handleSort('username')} style={{ padding: '10px 12px', cursor: 'pointer' }}>User {sortField === 'username' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('email')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('auth_provider')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Auth {sortField === 'auth_provider' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('role')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Role {sortField === 'role' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('is_active')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Status {sortField === 'is_active' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('last_login_at')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Last Login {sortField === 'last_login_at' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
              <th onClick={() => handleSort('created_at')} style={{ padding: '10px 12px', cursor: 'pointer' }}>Joined {sortField === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map(u => {
              const badge = roleBadgeColors[u.role] || roleBadgeColors.viewer;
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {u.profile_picture_url ? (
                        <img
                          src={u.profile_picture_url}
                          alt=""
                          style={{ width: '28px', height: '28px', borderRadius: '50%' }}
                        />
                      ) : (
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%',
                          background: '#e5e7eb', display: 'flex', alignItems: 'center',
                          justifyContent: 'center', fontSize: '12px', fontWeight: '600', color: '#6b7280'
                        }}>
                          {u.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span style={{ fontWeight: '500' }}>{u.username}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>{u.email}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <span style={{
                      fontSize: '11px', padding: '2px 6px', borderRadius: '4px',
                      background: u.auth_provider === 'google' ? '#fef3c7' : '#e0e7ff',
                      color: u.auth_provider === 'google' ? '#92400e' : '#3730a3'
                    }}>
                      {u.auth_provider === 'google' ? 'Google SSO' : 'Local'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <select
                      value={u.role}
                      onChange={e => handleRoleChange(u.id, e.target.value)}
                      disabled={saving === u.id}
                      style={{
                        padding: '4px 8px', fontSize: '12px', borderRadius: '4px',
                        border: '1px solid #d1d5db', background: badge.bg, color: badge.color,
                        fontWeight: '600', cursor: 'pointer'
                      }}
                    >
                      {ROLES.map(r => (
                        <option key={r} value={r}>
                          {r.charAt(0).toUpperCase() + r.slice(1).replace('_', ' ')}
                        </option>
                      ))}
                    </select>
                    {saving === u.id && <span style={{ marginLeft: '6px', fontSize: '11px' }}>Saving...</span>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    <button
                      onClick={() => handleToggleActive(u.id, !u.is_active)}
                      disabled={saving === u.id}
                      style={{
                        padding: '4px 10px', fontSize: '11px', borderRadius: '4px', border: 'none',
                        cursor: 'pointer', fontWeight: '600',
                        background: u.is_active ? '#d1fae5' : '#fee2e2',
                        color: u.is_active ? '#065f46' : '#991b1b'
                      }}
                    >
                      {u.is_active ? 'Active' : 'Disabled'}
                    </button>
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: '12px' }}>
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: '12px' }}>
                    {new Date(u.created_at).toLocaleDateString()}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement;
